import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { PublicStatusDemo } from "@/components/PublicStatusDemo";
import { Ship, Video, Mic, Camera, MessageSquare, GripVertical, Bell, ArrowRight, Anchor, MousePointerClick, SquareKanban, Check, Terminal, Monitor, PlayCircle, PenTool, Flame, BookOpen, Keyboard, Menu, X, ShieldCheck, Smile, Map, ThumbsUp, Target, Sparkles, Hash, FileText, History, Smartphone } from "lucide-react";
import WidgetDemo from "@/components/WidgetDemo";
import DashboardPreview from "@/components/DashboardPreview";
import EnterpriseContactDialog from "@/components/EnterpriseContactDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";

const captures = [
  { icon: Camera, label: "Screenshot", desc: "Pixel-perfect captures, marked up — add a voice note too.", bg: "bg-teal" },
  { icon: Video, label: "Screen recording", desc: "Watch the bug happen — with their narration on the mic.", bg: "bg-sunny" },
  { icon: Mic, label: "Voice note", desc: "Hear the frustration in their own words.", bg: "bg-orange text-white" },
  { icon: MessageSquare, label: "Text note", desc: "Quick written context, always attached.", bg: "bg-white" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, type: "spring", stiffness: 220, damping: 18 } }),
};

function LifetimeWidget({ promo }) {
  const [closed, setClosed] = useState(false);
  const lt = promo?.lifetime;
  if (!lt?.enabled || closed) return null;
  const spots = lt.spots || 30;
  const claimed = Math.min(lt.claimed || 0, spots);
  const remaining = lt.remaining ?? Math.max(0, spots - claimed);
  const pct = Math.min(100, Math.round((claimed / spots) * 100));
  const soldOut = lt.sold_out || remaining <= 0;
  return (
    <div data-testid="lifetime-widget"
      className="fixed z-[70] bottom-4 left-4 w-[300px] max-w-[calc(100vw-2rem)] animate-[ltpop_.5s_cubic-bezier(.34,1.56,.64,1)]">
      <div className="relative bg-ink text-cream border-2 border-ink rounded-2xl p-4 shadow-[6px_6px_0_0_#FF6B35] overflow-hidden">
        <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-orange/30 blur-xl" />
        <button onClick={() => setClosed(true)} data-testid="lifetime-widget-close" aria-label="Dismiss"
          className="absolute right-2 top-2 text-cream/50 hover:text-cream transition-colors z-10"><X className="h-4 w-4" /></button>
        <div className="flex items-center gap-2 mb-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-orange" />
          </span>
          <span className="text-[10px] font-800 uppercase tracking-[0.15em] text-orange">{soldOut ? "Offer closed" : "Limited offer · live"}</span>
        </div>
        <p className="font-display font-800 text-base leading-tight mb-1 flex items-center gap-1.5">
          <Flame className="h-4 w-4 text-orange shrink-0" /> {promo.lifetime.headline || "Founding lifetime membership"}
        </p>
        {soldOut ? (
          <p className="text-cream/70 text-xs font-600 mb-3">All {spots} lifetime spots have been claimed. Join the waitlist for the next drop.</p>
        ) : (
          <p className="text-cream/70 text-xs font-600 mb-3">Free forever for the first {spots} sign-ups. No card. No catch.</p>
        )}
        {!soldOut && (
          <div className="flex items-baseline gap-1.5 mb-1.5">
            <span data-testid="lifetime-remaining" className="font-display font-800 text-4xl text-sunny leading-none">{remaining}</span>
            <span className="text-cream/60 font-700 text-sm">/ {spots} spots left</span>
          </div>
        )}
        <div className="h-2.5 w-full rounded-full bg-cream/15 border border-cream/20 overflow-hidden mb-3">
          <div className="h-full bg-orange transition-all duration-700" style={{ width: `${soldOut ? 100 : pct}%` }} />
        </div>
        <Link to="/register" data-testid="lifetime-widget-cta"
          className="block text-center bg-orange text-white font-800 text-sm border-2 border-cream/20 rounded-xl py-2.5 hover:brightness-110 transition-all">
          {soldOut ? "Join the waitlist →" : "Claim your spot →"}
        </Link>
      </div>
    </div>
  );
}

export default function Landing() {
  const [promo, setPromo] = useState(null);
  const [promoDismissed, setPromoDismissed] = useState(() => {
    try { return localStorage.getItem("tugboat_promo_dismissed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/promo`).then((r) => r.json()).then(setPromo).catch(() => {});
  }, []);
  const showPromo = promo?.banner_enabled && !promoDismissed;
  const dismissPromo = () => {
    try { localStorage.setItem("tugboat_promo_dismissed", "1"); } catch {}
    setPromoDismissed(true);
  };
  return (
    <div className="min-h-screen wave-bg overflow-x-hidden">
      {showPromo && (
        <div data-testid="promo-banner" style={{ backgroundColor: promo.banner_bg || "#1A1A1A", color: promo.banner_fg || "#FFF7ED" }} className="relative text-center text-xs sm:text-sm font-700 px-10 py-2.5 flex items-center justify-center gap-3">
          <span data-testid="promo-banner-text">{promo.banner_text}</span>
          {(promo.banner_cta_text ?? "Start free →") && (() => {
            const ctaText = promo.banner_cta_text ?? "Start free →";
            const ctaUrl = promo.banner_cta_url || "/register";
            const isExternal = /^https?:\/\//i.test(ctaUrl);
            const cls = "shrink-0 underline decoration-orange decoration-2 underline-offset-2 hover:opacity-80 transition-opacity";
            return isExternal ? (
              <a href={ctaUrl} target="_blank" rel="noopener noreferrer" data-testid="promo-banner-cta" style={{ color: promo.banner_fg || "#FFF7ED" }} className={cls}>{ctaText}</a>
            ) : (
              <Link to={ctaUrl} data-testid="promo-banner-cta" style={{ color: promo.banner_fg || "#FFF7ED" }} className={cls}>{ctaText}</Link>
            );
          })()}
          <button onClick={dismissPromo} data-testid="promo-banner-dismiss" aria-label="Dismiss" className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100 transition-opacity">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <header className="h-18 py-4 px-6 sm:px-10 sticky top-0 bg-cream/85 backdrop-blur-md z-40 border-b-2 border-ink">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 bg-orange border-2 border-ink rounded-2xl flex items-center justify-center shadow-nb-sm">
              <Ship className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-700 text-2xl tracking-tight">Tugboat</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#about" data-testid="header-about-link" className="hidden md:inline text-sm font-700 hover:text-orange transition-colors px-3 py-2">About</a>
            <a href="#pricing" data-testid="header-pricing-link" className="hidden md:inline text-sm font-700 hover:text-orange transition-colors px-3 py-2">Pricing</a>
            <Link to="/docs" data-testid="header-docs-link" className="hidden md:inline text-sm font-700 hover:text-orange transition-colors px-3 py-2">Docs</Link>
            <a href="#faq" data-testid="header-faq-link" className="hidden md:inline text-sm font-700 hover:text-orange transition-colors px-3 py-2">FAQ</a>
            <Link to="/login" data-testid="header-login-link" className="hidden md:inline text-sm font-700 hover:text-orange transition-colors px-3 py-2">Sign in</Link>
            <Link to="/register" data-testid="header-register-link" className="nb-btn bg-orange text-white px-4 sm:px-5 py-2.5 text-sm">Get started</Link>

            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-trigger" aria-label="Open menu"
                  className="md:hidden h-10 w-10 flex items-center justify-center border-2 border-ink rounded-xl bg-white shadow-nb-sm hover:bg-sunny transition-colors">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-cream border-l-2 border-ink w-[78vw] max-w-xs p-0" data-testid="mobile-menu">
                <div className="p-6 border-b-2 border-ink flex items-center gap-2.5">
                  <div className="h-9 w-9 bg-orange border-2 border-ink rounded-xl flex items-center justify-center shadow-nb-sm">
                    <Ship className="h-4.5 w-4.5 text-white" />
                  </div>
                  <SheetTitle className="font-display font-700 text-xl tracking-tight">Tugboat</SheetTitle>
                </div>
                <nav className="flex flex-col p-4 gap-1">
                  <SheetClose asChild><a href="#about" data-testid="mobile-about-link" className="text-base font-700 px-4 py-3 rounded-xl hover:bg-sunny transition-colors">About</a></SheetClose>
                  <SheetClose asChild><a href="#pricing" data-testid="mobile-pricing-link" className="text-base font-700 px-4 py-3 rounded-xl hover:bg-sunny transition-colors">Pricing</a></SheetClose>
                  <SheetClose asChild><Link to="/docs" data-testid="mobile-docs-link" className="text-base font-700 px-4 py-3 rounded-xl hover:bg-sunny transition-colors">Docs</Link></SheetClose>
                  <SheetClose asChild><a href="#faq" data-testid="mobile-faq-link" className="text-base font-700 px-4 py-3 rounded-xl hover:bg-sunny transition-colors">FAQ</a></SheetClose>
                  <div className="h-px bg-ink/15 my-2" />
                  <SheetClose asChild><Link to="/login" data-testid="mobile-login-link" className="text-base font-700 px-4 py-3 rounded-xl hover:bg-sunny transition-colors">Sign in</Link></SheetClose>
                  <SheetClose asChild><Link to="/register" data-testid="mobile-register-link" className="nb-btn bg-orange text-white justify-center py-3 text-base mt-1">Get started</Link></SheetClose>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="min-w-0">
          <motion.div custom={0} initial="hidden" animate="show" variants={fadeUp}
            className="inline-flex items-center gap-2 bg-sunny border-2 border-ink rounded-full px-3.5 py-1.5 text-xs font-800 uppercase tracking-wide mb-6 shadow-nb-sm">
            <Anchor className="h-3.5 w-3.5" /> Beta feedback, hauled in for you
          </motion.div>
          <motion.h1 custom={1} initial="hidden" animate="show" variants={fadeUp}
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-700 tracking-tight leading-[0.95] mb-6">
            The feedback button testers <span className="text-orange">actually</span> use.
          </motion.h1>
          <motion.p custom={2} initial="hidden" animate="show" variants={fadeUp}
            className="text-lg text-ink/70 mb-8 max-w-lg leading-relaxed font-600">
            Drop one line of JavaScript into your beta. Testers grab a little floating tug to record their screen, talk through the bug, or snap a screenshot — and it sails straight to your dashboard, tagged by client.
          </motion.p>
          <motion.div custom={2.5} initial="hidden" animate="show" variants={fadeUp}
            className="flex flex-wrap gap-2 mb-8" data-testid="capability-chips">
            {[
              { icon: Video, label: "Screen recording" },
              { icon: Mic, label: "Voice notes" },
              { icon: Camera, label: "Annotated screenshots" },
              { icon: PlayCircle, label: "Session replay" },
              { icon: Map, label: "Roadmap & upvotes" },
              { icon: ShieldCheck, label: "PII-safe replay" },
              { icon: Terminal, label: "Console + network logs" },
              { icon: Monitor, label: "Env metadata" },
              { icon: Smartphone, label: "Device & OS tag" },
            ].map((c) => (
              <span key={c.label} className="inline-flex items-center gap-1.5 text-xs font-800 px-3 py-1.5 rounded-full border-2 border-ink bg-white shadow-nb-sm">
                <c.icon className="h-3.5 w-3.5 text-orange" /> {c.label}
              </span>
            ))}
          </motion.div>
          <motion.div custom={3} initial="hidden" animate="show" variants={fadeUp} className="flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-cta-button" className="nb-btn bg-orange text-white px-7 py-3.5 text-base">
              Start collecting feedback <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="#try-demo" data-testid="hero-demo-button" className="nb-btn bg-teal px-7 py-3.5 text-base">
              <MousePointerClick className="h-4 w-4" /> Try the live demo
            </a>
          </motion.div>
          <motion.pre custom={4} initial="hidden" animate="show" variants={fadeUp}
            className="mt-10 bg-ink text-sunny text-xs p-4 rounded-2xl overflow-x-auto font-mono border-2 border-ink shadow-nb">
{`<script src="${process.env.REACT_APP_BACKEND_URL}/widget.js"
        data-project-key="pk_your_key"></script>`}
          </motion.pre>
          <motion.p custom={5} initial="hidden" animate="show" variants={fadeUp} className="mt-3 text-xs font-700 text-ink/50">
            Works with React, Vue, Angular, Svelte & plain HTML — any web app, plus mobile web & PWAs.
          </motion.p>
        </div>

        <motion.div custom={2} initial="hidden" animate="show" variants={fadeUp} className="relative min-w-0">
          <div className="nb-card p-3 rotate-1">
            <div className="bg-cream rounded-2xl border-2 border-ink overflow-hidden">
              <div className="h-10 border-b-2 border-ink flex items-center gap-1.5 px-4 bg-white">
                <div className="h-3 w-3 rounded-full border-2 border-ink bg-danger" />
                <div className="h-3 w-3 rounded-full border-2 border-ink bg-sunny" />
                <div className="h-3 w-3 rounded-full border-2 border-ink bg-grass" />
                <span className="ml-3 text-xs text-ink/60 font-mono font-600">app.yourfintech.com</span>
              </div>
              <div className="p-6 space-y-3">
                <div className="h-3 w-1/3 bg-ink/10 rounded-full" />
                <div className="h-24 bg-white border-2 border-ink/15 rounded-2xl" />
                <div className="grid grid-cols-3 gap-3">
                  <div className="h-16 bg-teal/30 border-2 border-ink/15 rounded-xl" />
                  <div className="h-16 bg-sunny/40 border-2 border-ink/15 rounded-xl" />
                  <div className="h-16 bg-orange/20 border-2 border-ink/15 rounded-xl" />
                </div>
              </div>
            </div>
          </div>
          {/* Floating widget mock */}
          <motion.div
            animate={{ y: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            className="absolute -bottom-5 -right-3 flex items-center gap-2 bg-orange text-white pl-2 pr-4 py-3 rounded-full border-2 border-ink shadow-nb">
            <GripVertical className="h-4 w-4 opacity-60" />
            <Ship className="h-5 w-5" />
            <span className="text-sm font-800">Feedback</span>
          </motion.div>
        </motion.div>
      </section>

      {/* Interactive demo */}
      <section id="try-demo" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 min-w-0">
            <div className="inline-flex items-center gap-2 bg-teal border-2 border-ink rounded-full px-3.5 py-1.5 text-xs font-800 uppercase tracking-wide mb-5 shadow-nb-sm">
              <MousePointerClick className="h-3.5 w-3.5" /> Live demo — no signup
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-700 tracking-tight mb-4">
              Take it for a spin. <span className="text-orange">Right here.</span>
            </h2>
            <p className="text-lg text-ink/70 font-600 mb-6 max-w-md leading-relaxed">
              This is a real beta app (pretend it's yours). Click the floating <b>Feedback</b> tug in the corner, drag it around, and try every capture flow — screenshot, screen recording, voice, a quick note, even "What's New."
            </p>
            <ul className="space-y-3">
              {[
                "Drag the button anywhere on the page",
                "Open the panel and pick a capture type",
                "Send a note and watch it sail off",
                "Peek at the changelog testers would see",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm font-700">
                  <span className="h-6 w-6 bg-sunny border-2 border-ink rounded-lg flex items-center justify-center shrink-0">
                    <Anchor className="h-3.5 w-3.5" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-6 inline-flex items-center gap-2.5 text-sm font-700 bg-white border-2 border-ink rounded-xl px-3.5 py-2.5 shadow-nb-sm" data-testid="hero-hotkey-hint">
              <Keyboard className="h-4 w-4 text-orange shrink-0" />
              <span>Or just press
                <kbd className="font-mono text-xs bg-sunny border-2 border-ink rounded-md px-1.5 py-0.5 mx-0.5">Ctrl</kbd>
                <kbd className="font-mono text-xs bg-sunny border-2 border-ink rounded-md px-1.5 py-0.5 mx-0.5">Shift</kbd>
                <kbd className="font-mono text-xs bg-sunny border-2 border-ink rounded-md px-1.5 py-0.5 mx-0.5">K</kbd>
                to open it
              </span>
            </div>
          </div>
          <div className="lg:col-span-7 min-w-0">
            <WidgetDemo />
            <p className="text-center text-xs font-700 text-ink/50 mt-3">
              ✦ Fully interactive demo — nothing is sent or stored
            </p>
          </div>
        </div>
      </section>

      {/* Inside the dashboard */}
      <section id="dashboard" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1 min-w-0">
            <DashboardPreview />
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2 min-w-0">
            <div className="inline-flex items-center gap-2 bg-orange text-white border-2 border-ink rounded-full px-3.5 py-1.5 text-xs font-800 uppercase tracking-wide mb-5 shadow-nb-sm">
              <SquareKanban className="h-3.5 w-3.5" /> On the other side
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-700 tracking-tight mb-4">
              The demo captures. <span className="text-orange">This is where you ship.</span>
            </h2>
            <p className="text-lg text-ink/70 font-600 mb-6 max-w-md leading-relaxed">
              Every capture lands in one tidy inbox — video, voice, screenshots and notes — ready to triage, tag, and push to Jira. When the ticket closes, Tugboat resolves it for you automatically.
            </p>
            <ul className="space-y-3">
              {[
                "Triage by status, tag, type or subject",
                "Watch recordings & hear voice notes inline",
                "One-click to Jira — auto-resolve on close",
                "Trends & geo-analytics on who's testing where",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm font-700">
                  <span className="h-6 w-6 bg-teal border-2 border-ink rounded-lg flex items-center justify-center shrink-0">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <Link to="/register" data-testid="dashboard-cta-button" className="nb-btn bg-orange text-white px-6 py-3 text-sm mt-7 inline-flex">
              Start free — it's your demo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Captures */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <p className="label-overline mb-3">Four ways to capture</p>
        <h2 className="font-display text-4xl font-700 tracking-tight mb-12 max-w-xl">Everything your tester needs, zero friction.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {captures.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06, type: "spring", stiffness: 200, damping: 18 }}
              className={`nb-card nb-card-hover p-6 ${c.bg}`}>
              <div className="h-12 w-12 bg-white border-2 border-ink rounded-2xl flex items-center justify-center mb-4 shadow-nb-sm">
                <c.icon className="h-6 w-6 text-ink" />
              </div>
              <h3 className="font-display font-700 text-xl mb-1">{c.label}</h3>
              <p className="text-sm leading-relaxed font-600 opacity-80">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Developer-grade capture */}
      <section id="capture" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 scroll-mt-24">
        <div className="nb-card bg-ink text-cream p-8 lg:p-12">
          <p className="label-overline mb-3 text-orange">Developer-grade capture</p>
          <h2 className="font-display text-4xl font-700 tracking-tight mb-3 max-w-2xl">Not just a screenshot — the whole crime scene.</h2>
          <p className="text-cream/70 font-600 max-w-2xl mb-10 leading-relaxed">
            Every report arrives with the technical context developers actually need to reproduce a bug — automatically attached, no tester effort required.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: PlayCircle, label: "Session replay", desc: "An rrweb DVR of the last ~30s before they hit send. Watch exactly what they did.", tier: "Team+" },
              { icon: ShieldCheck, label: "PII redaction", desc: "Emails, phones & card numbers are auto-scrubbed from replays client-side. Block any region with one attribute.", tier: "All plans" },
              { icon: Terminal, label: "Console & network logs", desc: "The console errors and failed requests captured at the moment of the bug.", tier: "All plans" },
              { icon: Monitor, label: "Environment metadata", desc: "OS, browser, viewport, timezone & more — auto-detected on every submission.", tier: "All plans" },
              { icon: PenTool, label: "Annotated screenshots", desc: "Testers draw boxes, arrows & freehand right on the capture before sending.", tier: "All plans" },
              { icon: Smile, label: "Sentiment ratings", desc: "A one-tap 😠→😍 emoji scale on every report — turn notes into an at-a-glance signal.", tier: "All plans" },
            ].map((c, i) => (
              <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.06, type: "spring", stiffness: 200, damping: 18 }}
                className="bg-cream text-ink border-2 border-cream rounded-2xl p-5">
                <div className="h-11 w-11 bg-white border-2 border-ink rounded-xl flex items-center justify-center mb-4 shadow-nb-sm">
                  <c.icon className="h-5 w-5 text-orange" />
                </div>
                <h3 className="font-display font-700 text-lg mb-1">{c.label}</h3>
                <p className="text-sm leading-relaxed font-600 text-ink/70 mb-3">{c.desc}</p>
                <span className="inline-block text-[10px] font-800 uppercase tracking-wide px-2 py-0.5 rounded-full border-2 border-ink bg-sunny">{c.tier}</span>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-2 text-cream/80 font-700 text-sm">
            <span className="inline-flex items-center gap-1.5 text-xs font-800 px-2.5 py-1 rounded-full border-2 border-cream/30 bg-danger text-white"><Flame className="h-3.5 w-3.5" /> Errors</span>
            Reports carrying console errors or failed requests get flagged in your inbox — filter to the highest-signal bugs first.
          </div>
        </div>
      </section>

      {/* Beyond capture — triage, resolve, close the loop */}
      <section id="workflow" className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <p className="label-overline mb-3">Beyond capture</p>
        <h2 className="font-display text-4xl font-700 tracking-tight mb-3 max-w-2xl">Triage it, resolve it, close the loop.</h2>
        <p className="text-ink/70 font-600 max-w-2xl mb-12 leading-relaxed">Tugboat isn't just an inbox. Number, track and report every submission — and keep your testers in the loop automatically.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: Hash, label: "Canonical numbers", desc: "Every submission gets a per-project number like #42 — reference bugs like a real tracker.", bg: "bg-white" },
            { icon: History, label: "Full activity log", desc: "Status, tags, notes & replies — logged with who, when and which submission (number + subject), project-wide.", bg: "bg-sunny" },
            { icon: MessageSquare, label: "Internal comments", desc: "Team-only notes on any submission. Discuss, assign and decide — without the noise.", bg: "bg-white" },
            { icon: FileText, label: "PDF audit report", desc: "Export every change over any date range as a branded report — with a Submission column and full note & reply text.", bg: "bg-teal" },
            { icon: Bell, label: "Tester updates & replies", desc: "Testers opt in for email updates and get a public status page to reply back — a real two-way conversation.", bg: "bg-white" },
            { icon: Sparkles, label: "Auto-publish fixes", desc: "Mark a bug Resolved and it auto-posts to your widget's What's New. Guided prompts help non-tech testers, too.", bg: "bg-orange text-white" },
          ].map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06, type: "spring", stiffness: 200, damping: 18 }}
              className={`nb-card nb-card-hover p-6 ${c.bg}`}>
              <div className="h-12 w-12 bg-white border-2 border-ink rounded-2xl flex items-center justify-center mb-4 shadow-nb-sm">
                <c.icon className="h-6 w-6 text-ink" />
              </div>
              <h3 className="font-display font-700 text-xl mb-1">{c.label}</h3>
              <p className="text-sm leading-relaxed font-600 opacity-80">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Tester public status page — animated demo */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-4">
        <div className="nb-card bg-cream p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="h-12 w-12 bg-white border-2 border-ink rounded-2xl flex items-center justify-center mb-5 shadow-nb-sm">
              <Bell className="h-6 w-6 text-orange" />
            </div>
            <p className="label-overline mb-3">A real two-way conversation</p>
            <h2 className="font-display text-4xl font-700 tracking-tight mb-4">Every tester gets their own status page.</h2>
            <p className="text-ink/75 leading-relaxed max-w-md font-600">
              Every tester gets a private link to their report the moment they hit send — shown right on the widget's "Thank you" screen, ready to bookmark. They watch the <b>status</b> move, read your <b>public replies</b>, and <b>reply back</b> with more detail — which lands straight in your dashboard. Opt in and they're emailed on every update too. Internal notes stay private. Resolve it and it can auto-post to your changelog.
            </p>
            <p className="text-sm text-ink/55 font-700 mt-5">No login. No app to install. Just a link.</p>
          </div>
          <PublicStatusDemo />
        </div>
      </section>

      {/* Two-way */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <div className="nb-card bg-teal p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="h-12 w-12 bg-white border-2 border-ink rounded-2xl flex items-center justify-center mb-5 shadow-nb-sm">
              <Bell className="h-6 w-6 text-orange" />
            </div>
            <p className="label-overline mb-3">Close the loop</p>
            <h2 className="font-display text-4xl font-700 tracking-tight mb-4">Sail a changelog — and a roadmap — right back to them.</h2>
            <p className="text-ink/75 leading-relaxed max-w-md font-600">
              Ship a fix? Post a "What's New" note. Planning ahead? Share a public <b>Roadmap</b> and let testers <b>upvote</b> what matters most — anonymously, one vote per browser. Every tester running your beta sees it inside the same floating widget, so they know you actually listened.
            </p>
            <p className="text-ink/70 leading-relaxed max-w-md font-600 mt-4 flex items-start gap-2">
              <Target className="h-5 w-5 text-orange shrink-0 mt-0.5" />
              <span><b>Audience targeting:</b> aim any changelog or roadmap post at a cohort — by plan, role or segment — so you can announce betas to the right people without confusing everyone else.</span>
            </p>
          </div>
          <div className="space-y-4 -rotate-1">
            <div className="nb-card bg-white p-6 space-y-4">
              <p className="text-[11px] font-800 uppercase tracking-wide text-ink/50 flex items-center gap-1.5"><Bell className="h-3.5 w-3.5 text-orange" /> What's new</p>
              {[
                { v: "v1.4.2", t: "Fixed transfer timeout", b: "Wire transfers no longer hang on confirmation." },
                { v: "v1.4.1", t: "Faster dashboard load", b: "Cut initial render time by 40%." },
              ].map((u) => (
                <div key={u.v} className="border-l-4 border-orange pl-4">
                  <span className="font-mono text-xs text-orange font-600">{u.v}</span>
                  <h4 className="font-700 text-sm mt-0.5">{u.t}</h4>
                  <p className="text-sm text-ink/60 font-600">{u.b}</p>
                </div>
              ))}
            </div>
            <div className="nb-card bg-white p-6 space-y-3">
              <p className="text-[11px] font-800 uppercase tracking-wide text-ink/50 flex items-center gap-1.5"><Map className="h-3.5 w-3.5 text-orange" /> Roadmap</p>
              {[
                { t: "Dark mode", s: "Planned", votes: 128, sc: "bg-white" },
                { t: "CSV export", s: "In progress", votes: 84, sc: "bg-sunny" },
              ].map((r) => (
                <div key={r.t} className="flex items-center gap-3 border-2 border-ink rounded-xl p-2.5 shadow-nb-sm">
                  <div className="flex flex-col items-center justify-center min-w-[42px] border-2 border-ink rounded-lg bg-cream px-1.5 py-1">
                    <ThumbsUp className="h-3.5 w-3.5 text-orange" />
                    <span className="text-xs font-800 leading-none mt-0.5">{r.votes}</span>
                  </div>
                  <div>
                    <span className={`inline-block text-[9px] font-800 uppercase tracking-wide border-2 border-ink rounded px-1.5 py-0.5 mb-1 ${r.sc}`}>{r.s}</span>
                    <h4 className="font-700 text-sm leading-tight">{r.t}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
        <div className="nb-card bg-white p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
            <div>
              <p className="label-overline mb-3">Plays nice with your stack</p>
              <h2 className="font-display text-4xl font-700 tracking-tight max-w-xl">Push feedback into the tools your team already lives in.</h2>
            </div>
            <p className="text-ink/60 font-600 max-w-sm">One click turns a tester's recording into a ticket — screenshots, video and voice attached. <span className="text-orange font-800">Jira is live today;</span> more on the way.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { name: "Jira", live: true },
              { name: "Linear", live: false },
              { name: "GitHub", live: false },
              { name: "Asana", live: false },
              { name: "Trello", live: false },
              { name: "ClickUp", live: false },
            ].map((t) => (
              <div key={t.name}
                className={`relative flex flex-col items-center justify-center gap-2 p-5 border-2 border-ink rounded-2xl text-center ${t.live ? "bg-teal shadow-nb-sm" : "bg-cream"}`}>
                <SquareKanban className="h-6 w-6" />
                <span className="font-800 text-sm">{t.name}</span>
                <span className={`text-[10px] font-800 uppercase tracking-wide px-2 py-0.5 rounded-full border-2 border-ink ${t.live ? "bg-grass text-white" : "bg-white text-ink/50"}`}>
                  {t.live ? "Live" : "Soon"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Why */}
      <section id="about" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <p className="label-overline mb-3">Why Tugboat</p>
            <h2 className="font-display text-4xl sm:text-5xl font-700 tracking-tight">The feedback loop is broken. We're fixing it.</h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-lg text-ink/70 font-600 leading-relaxed">
            <p>The need for in-app feedback is greater than ever — yet almost nobody has it. Every team struggles with the same thing: <span className="text-ink font-800">how do you actually hear from the people using your product?</span> Bug reports get lost in email threads, Slack messages and vague "it's broken" notes. The loop between the user who hit the problem and the developer who can fix it has all but disappeared.</p>
            <p>Screenshots only tell half the story. The real signal lives in <span className="text-ink font-800">clean video of what went wrong</span> and the <span className="text-ink font-800">user's own voice</span> explaining it — context you simply can't get from a text box.</p>
            <p>That's why we built Tugboat: a tiny floating button that turns any tester into a perfect bug reporter, and hauls that rich, in-context feedback straight to the team that can act on it. We close the gap — so you can ship with confidence in a world that moves faster every day.</p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 sm:px-10 py-20 scroll-mt-24">
        <div className="text-center mb-12">
          <p className="label-overline mb-3">Pricing</p>
          <h2 className="font-display text-4xl sm:text-5xl font-700 tracking-tight">Simple plans that scale with your crew.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {[
            { name: "Solo", price: "$0", tag: "Forever free", bg: "bg-white", feats: ["1 project", "100 feedback / mo", "All capture types", "7-day history"], cta: "Start free" },
            { name: "Team", price: "$39", tag: "per month", bg: "bg-sunny", popular: true, feats: ["Unlimited projects", "5 teammates", "Jira + integrations", "Unlimited history", "Changelog publishing"], cta: "Start 14-day trial" },
            { name: "Business", price: "$149", tag: "per month", bg: "bg-teal", feats: ["Everything in Team", "Unlimited teammates", "Custom branding & white-label", "Member & Viewer roles", "Priority support"], cta: "Talk to us" },
            { name: "Enterprise", price: "Custom", tag: "contact us", bg: "bg-ink text-cream", enterprise: true, feats: ["Everything in Business", "AI feedback intelligence (soon)", "SSO / SAML / SCIM (soon)", "Audit logs & SLA", "Dedicated support", "Custom contract & invoicing"], cta: "Talk to sales" },
          ].map((p) => (
            <div key={p.name} className={`nb-card ${p.bg} p-8 flex flex-col relative ${p.popular ? "lg:-translate-y-2" : ""}`}>
              {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange text-white text-xs font-800 uppercase tracking-wide px-3 py-1 rounded-full border-2 border-ink">Most popular</span>}
              <h3 className="font-display font-700 text-2xl">{p.name}</h3>
              <div className="flex items-end gap-1.5 mt-3 mb-1">
                <span className="font-display text-5xl font-700">{p.price}</span>
                <span className={`text-sm font-700 mb-1.5 ${p.enterprise ? "text-cream/60" : "text-ink/60"}`}>{p.tag}</span>
              </div>
              <ul className="space-y-2.5 mt-5 mb-7 flex-1">
                {p.feats.map((f) => (
                  <li key={f} className={`flex items-center gap-2 text-sm font-700 ${p.enterprise ? "text-cream/90" : ""}`}>
                    <span className={`h-5 w-5 border-2 border-ink rounded-md flex items-center justify-center shrink-0 ${p.enterprise ? "bg-orange" : "bg-white"}`}><Check className={`h-3 w-3 ${p.enterprise ? "text-white" : ""}`} /></span>{f}
                  </li>
                ))}
              </ul>
              {p.enterprise
                ? <EnterpriseContactDialog triggerClassName="nb-btn bg-orange text-white w-full py-3 text-sm" triggerLabel={p.cta} />
                : <Link to="/register" className="nb-btn bg-orange text-white w-full py-3 text-sm">{p.cta}</Link>}
            </div>
          ))}
        </div>
        <p className="text-center text-xs font-700 text-ink/40 mt-6">Prices indicative — billing isn't switched on yet during beta.</p>

        {/* Comparison matrix */}
        <div className="nb-card p-0 overflow-hidden mt-12" data-testid="pricing-comparison">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="border-b-2 border-ink bg-sand">
                  <th className="text-left font-display font-700 text-base px-5 py-4">Compare plans</th>
                  <th className="font-800 px-4 py-4 text-center">Solo</th>
                  <th className="font-800 px-4 py-4 text-center bg-sunny">Team</th>
                  <th className="font-800 px-4 py-4 text-center">Business</th>
                  <th className="font-800 px-4 py-4 text-center bg-ink text-cream">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Projects", "1", "Unlimited", "Unlimited", "Unlimited"],
                  ["Teammates", "1", "5", "Unlimited", "Unlimited"],
                  ["All capture types", true, true, true, true],
                  ["Annotated screenshots", true, true, true, true],
                  ["Console, network & env capture", true, true, true, true],
                  ["Session replay (rrweb)", false, true, true, true],
                  ["Feedback / month", "100", "Unlimited", "Unlimited", "Unlimited"],
                  ["Recording length", "30s", "3 min", "10 min", "Custom"],
                  ["History", "7 days", "Unlimited", "Unlimited", "Unlimited"],
                  ["Subjects & topics", true, true, true, true],
                  ["Changelog \u201cWhat\u2019s New\u201d", true, true, true, true],
                  ["Roadmap & anonymous upvotes", true, true, true, true],
                  ["Sentiment (emoji) ratings", true, true, true, true],
                  ["PII redaction & Shadow DOM", true, true, true, true],
                  ["Audience targeting (cohorts)", true, true, true, true],
                  ["Ticketing integrations (Jira…)", false, true, true, true],
                  ["Custom branding / white-label", false, false, true, true],
                  ["Member & Viewer roles", false, false, true, true],
                  ["Priority support", false, false, true, true],
                  ["AI feedback intelligence", false, false, false, "Soon"],
                  ["SSO (SAML / Okta) + SCIM", false, false, false, "Soon"],
                  ["Audit logs & SLA", false, false, false, true],
                  ["Dedicated support + custom contract", false, false, false, true],
                ].map((row, ri) => (
                  <tr key={ri} className="border-b-2 border-ink/10 last:border-0">
                    <td className="px-5 py-3 font-700">{row[0]}</td>
                    {row.slice(1).map((cell, ci) => (
                      <td key={ci} className={`px-4 py-3 text-center font-700 ${ci === 1 ? "bg-sunny/40" : ""} ${ci === 3 ? "bg-ink/5" : ""}`}>
                        {cell === true ? <Check className="h-4 w-4 mx-auto text-grass" />
                          : cell === false ? <span className="text-ink/25">—</span>
                          : <span>{cell}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 sm:px-10 py-20 scroll-mt-24">
        <div className="text-center mb-10">
          <p className="label-overline mb-3">FAQ</p>
          <h2 className="font-display text-4xl sm:text-5xl font-700 tracking-tight">Questions, answered.</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {[
            { q: "Which frameworks does Tugboat work with?", a: "Any of them. Tugboat is a single <script> tag that injects a self-contained widget, so it works with React, Vue, Angular, Svelte, Next.js, or plain HTML — it doesn't care what your app is built with." },
            { q: "Does it work on mobile apps?", a: "It works everywhere the web runs: mobile web, PWAs, and in-app web views, plus Flutter Web. Fully native iOS/Android, React Native and Flutter (native) builds aren't supported by the script yet — a native SDK for those is on our roadmap." },
            { q: "How do I install it?", a: "Create a project, copy the one-line snippet from your project's Settings tab, and paste it before your closing </body> tag. The floating button appears instantly — no build step, no package to install." },
            { q: "What can testers capture?", a: "Screenshots (with markup + an optional attached voice note), screen recordings with microphone narration, voice notes and written notes — with an optional subject/topic you define, plus their name/contact if they choose to share it." },
            { q: "Is the data secure?", a: "Feedback and media are stored privately and only accessible to your team. Recordings are user-initiated and explicit. Integration credentials (like Jira tokens) are encrypted at rest." },
            { q: "Can I match it to my brand?", a: "Yes — on the Business plan you can white-label the widget per project: your own logo or a preset button icon (ship, bug, lightbulb, megaphone & more), accent color, button text & shape, a custom panel headline, and fully editable widget copy to remove every trace of Tugboat." },
            { q: "Is the widget lightweight? Will it slow my site down?", a: "Very. The core widget is ~22KB gzipped (one script tag) and renders entirely inside its own Shadow DOM — so its styles can't clash with your app's markup, and your CSS can't break it. Session replay uses rrweb (~119KB), but it's only loaded for projects on a plan with replay enabled — if you don't use replay, that code never downloads." },
            { q: "Is it safe with user PII?", a: "Yes. All form inputs are masked in session replays by default, and Tugboat auto-redacts emails, phone numbers and card-like numbers from recorded text — client-side, before anything is sent. You can also block or mask any element on the page with a single data-tugboat-block or data-tugboat-mask attribute. Redaction is on by default and toggleable per project." },
            { q: "Can testers vote on what to build next?", a: "Yes — the widget includes a Roadmap panel showing what's Planned, In progress and Released, and testers can upvote items anonymously (one vote per browser, no login). You can also target roadmap and changelog posts to specific cohorts (by plan, role or segment) so betas go to the right people." },
            { q: "Can I keep testers in the loop after they report something?", a: "Yes. Testers can opt in to email updates when they submit. Your team can post a public reply (which emails them) or an internal team-only note. Each submission gets a secure public status page where the tester sees the status and can reply back — a real two-way conversation, right in your dashboard. Mark a bug Resolved and it can auto-publish a 'Fixed' entry to the widget's What's New." },
            { q: "How do I track and report on feedback across the team?", a: "Every submission gets a canonical number (like #42) and a full activity log — status changes, tags, comments and replies, all with who and when. A project-wide Activity tab shows every change, and managers can export a branded PDF audit report over any date range." },
            { q: "My testers aren't technical — how do I get useful reports?", a: "Turn on Guided submissions per project: instead of a blank note box, the widget shows short prompts ('What were you trying to do? / expected? / actually happened?') plus an optional 'I'm reporting as…' persona picker, and assembles the answers into one clean, structured report." },
            { q: "What does it cost?", a: "There's a free Solo plan to get started, Team at $39/mo for collaboration and integrations (unlimited projects, 5 teammates and Jira), and Business at $149/mo for custom branding & white-label, Member & Viewer roles and priority support. Need SSO/SCIM or a custom contract? That's our Enterprise plan — talk to sales. (Billing turns on after beta.)" },
          ].map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="nb-card px-5 border-2">
              <AccordionTrigger className="font-display font-700 text-base hover:no-underline text-left" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="text-ink/70 font-600 text-sm leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24 text-center">
        <div className="h-14 w-14 bg-orange border-2 border-ink rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-nb">
          <Ship className="h-7 w-7 text-white" />
        </div>
        <h2 className="font-display text-4xl sm:text-5xl font-700 tracking-tight mb-6">Ship faster. Hear everything.</h2>
        <Link to="/register" data-testid="footer-cta-button" className="nb-btn bg-orange text-white px-8 py-4 text-base">
          Create your free account <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <footer className="border-t-2 border-ink py-8 px-4 text-center text-sm text-ink/60 font-700">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Ship className="h-4 w-4 text-orange shrink-0" /> Tugboat — beta feedback, hauled in
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-orange transition-colors">Privacy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-orange transition-colors">Terms</Link>
          <Link to="/docs" data-testid="footer-docs-link" className="hover:text-orange transition-colors">Docs</Link>
          <a href="/llms.txt" data-testid="footer-llms-link" className="inline-flex items-center gap-1 hover:text-orange transition-colors"><Sparkles className="h-3.5 w-3.5 text-orange" /> Docs for AI</a>
          <Link to="/contact" data-testid="footer-contact-link" className="hover:text-orange transition-colors">Contact</Link>
          <span className="text-ink/30 hidden sm:inline">·</span>
          <a href="mailto:hello@tugboatapp.com" className="hover:text-orange transition-colors break-all">hello@tugboatapp.com</a>
        </div>
      </footer>
      <LifetimeWidget promo={promo} />
    </div>
  );
}
